/**
 * IMPORT CSV - ÉTAPE 1 : UPLOAD
 * Drag & drop de fichier avec react-dropzone
 */

import { useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { Upload, FileText, AlertCircle } from 'lucide-react';
import { Button } from '../ui';
import type { CSVParsedData } from '../../types/csv';
import { parseCSVFile } from '../../lib/csv-import';

interface ImportStepUploadProps {
  onFileLoaded: (data: CSVParsedData) => void;
  onError: (error: string) => void;
}

export function ImportStepUpload({ onFileLoaded, onError }: ImportStepUploadProps) {
  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      const file = acceptedFiles[0];

      if (!file) {
        onError('Aucun fichier sélectionné');
        return;
      }

      // Vérifier le type
      if (!file.name.endsWith('.csv')) {
        onError('Le fichier doit être au format CSV');
        return;
      }

      // Vérifier la taille (max 10MB)
      const MAX_SIZE = 10 * 1024 * 1024;
      if (file.size > MAX_SIZE) {
        onError('Le fichier est trop volumineux (maximum 10 MB)');
        return;
      }

      try {
        const parsedData = await parseCSVFile(file);

        if (parsedData.rowCount === 0) {
          onError('Le fichier CSV est vide');
          return;
        }

        if (parsedData.headers.length === 0) {
          onError('Aucune colonne détectée dans le fichier');
          return;
        }

        onFileLoaded(parsedData);
      } catch (error) {
        onError(error instanceof Error ? error.message : 'Erreur lors de la lecture du fichier');
      }
    },
    [onFileLoaded, onError]
  );

  const { getRootProps, getInputProps, isDragActive, isDragReject } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv'],
      'text/plain': ['.csv'],
    },
    maxFiles: 1,
    maxSize: 10 * 1024 * 1024,
  });

  return (
    <div className="space-y-8">
      <div className="text-center">
        <h3 className="text-2xl font-semibold mb-3">Importer depuis un fichier CSV</h3>
        <p className="text-base text-gray-600">
          Glissez-déposez votre fichier ou cliquez pour sélectionner
        </p>
      </div>

      <div
        {...getRootProps()}
        className={`
          border-2 border-dashed rounded-lg p-20 text-center cursor-pointer
          transition-colors duration-200
          ${
            isDragActive && !isDragReject
              ? 'border-blue-500 bg-blue-50'
              : isDragReject
                ? 'border-red-500 bg-red-50'
                : 'border-gray-300 hover:border-gray-400 bg-gray-50'
          }
        `}
      >
        <input {...getInputProps()} />

        <div className="flex flex-col items-center space-y-6">
          {isDragActive ? (
            <>
              <Upload className="h-20 w-20 text-blue-500" />
              <p className="text-xl font-medium text-blue-700">Déposez le fichier ici</p>
            </>
          ) : (
            <>
              <FileText className="h-20 w-20 text-gray-400" />
              <div className="space-y-2">
                <p className="text-lg font-medium text-gray-700">
                  Glissez-déposez un fichier CSV ici
                </p>
                <p className="text-base text-gray-500">ou cliquez pour parcourir vos fichiers</p>
              </div>
              <Button variant="outline" size="lg" type="button" className="mt-4">
                Sélectionner un fichier
              </Button>
            </>
          )}
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
        <div className="flex items-start space-x-4">
          <AlertCircle className="h-6 w-6 text-blue-600 mt-0.5 flex-shrink-0" />
          <div className="text-base text-blue-800 space-y-2">
            <p className="font-medium text-lg">Format attendu :</p>
            <ul className="list-disc list-inside space-y-1 text-sm">
              <li>Fichier CSV avec séparateur point-virgule (;) ou virgule (,)</li>
              <li>Première ligne = en-têtes de colonnes</li>
              <li>Encodage UTF-8 recommandé</li>
              <li>Taille maximale : 10 MB</li>
              <li>Dates au format DD/MM/YYYY</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
